import { isNetworkError, isRejection } from "@/utils/network";

const POLL_DELAYS_MS = [500, 1000, 2000];
const TASK_TIMEOUT_MS = 6 * 60 * 1000;
const TASK_PICKUP_TIMEOUT_MS = 90 * 1000;
const FAILED_TASK_STATUSES = new Set(["FAILURE", "REVOKED"]);
const MAX_CONSECUTIVE_POLL_ERRORS = 5;

export const TASK_ERRORS = Object.freeze({
  FAILED: "TASK_FAILED",
  NOT_PICKED_UP: "TASK_NOT_PICKED_UP",
  TIMED_OUT: "TASK_TIMED_OUT",
});

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const taskError = (message, code, detail) =>
  Object.assign(new Error(message), { code, detail });

const isTransientPollError = (error) =>
  isNetworkError(error) || (!!error?.response && !isRejection(error));

export default async function waitForTask(
  client,
  url,
  taskLabel,
  { timeoutMs = TASK_TIMEOUT_MS, pickupTimeoutMs = TASK_PICKUP_TIMEOUT_MS } = {}
) {
  const startedAt = Date.now();
  const deadline = startedAt + timeoutMs;
  const delays = [...POLL_DELAYS_MS];
  let pollErrors = 0;
  while (Date.now() < deadline) {
    await wait(delays.length > 1 ? delays.shift() : delays[0]);
    let data;
    try {
      ({ data } = await client.get(url));
      pollErrors = 0;
    } catch (error) {
      pollErrors += 1;
      if (
        isTransientPollError(error) &&
        pollErrors < MAX_CONSECUTIVE_POLL_ERRORS
      )
        continue;
      throw error;
    }
    if (data.status === "SUCCESS") return data;
    if (FAILED_TASK_STATUSES.has(data.status)) {
      throw taskError(
        `${taskLabel} ${data.status}`,
        TASK_ERRORS.FAILED,
        data.detail
      );
    }
    if (data.status === "PENDING" && Date.now() - startedAt > pickupTimeoutMs) {
      throw taskError(
        `${taskLabel} never picked up`,
        TASK_ERRORS.NOT_PICKED_UP
      );
    }
  }
  throw taskError(`${taskLabel} timed out`, TASK_ERRORS.TIMED_OUT);
}
